import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type RrpPreview } from '../api';
import { Button } from '../design';

const KINDS = ['products', 'prices', 'stocks'] as const;

export function SyncPage() {
  const qc = useQueryClient();
  const health = useQuery({ queryKey: ['health'], queryFn: api.health, refetchInterval: 10_000 });
  const runs = useQuery({ queryKey: ['sync-runs'], queryFn: () => api.syncRuns(), refetchInterval: 5_000 });
  const trigger = useMutation({
    mutationFn: (kind: typeof KINDS[number]) => api.triggerSync(kind),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sync-runs'] }),
  });

  return (
    <>
      <div className="page-header">
        <h2>Синхронизация</h2>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Состояние</h3>
        {health.data ? (
          <>
            <div>База данных: {health.data.db}</div>
            <div>
              Ozon API:{' '}
              {health.data.ozon.configured
                ? `✅ настроен (источник: ${health.data.ozon.source === 'db' ? 'настройки в БД' : '.env файл'})`
                : '❌ ключи не заданы — открой страницу «Настройки»'}
            </div>
            <div>Ozon Premium: {health.data.ozon.premium ? 'Да' : 'Нет'}</div>
          </>
        ) : 'Загрузка…'}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Ручной запуск</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {KINDS.map((kind) => (
            <Button key={kind} variant="secondary" onClick={() => trigger.mutate(kind)} disabled={trigger.isPending}>
              {kind}
            </Button>
          ))}
        </div>
        {trigger.isPending && <div className="muted" style={{ marginTop: 8 }}>Отправка в очередь…</div>}
        {trigger.data && (
          <div style={{ marginTop: 8, color: 'var(--green)', fontSize: 13 }}>
            ✓ Задача поставлена в очередь (jobId: {trigger.data.jobId ?? '—'}). Прогресс смотри в таблице ниже.
          </div>
        )}
        {trigger.error && <div style={{ color: 'var(--red)', marginTop: 8, fontSize: 13 }}>Ошибка: {(trigger.error as Error).message}</div>}
      </div>

      <RrpBlock />

      <h3>История запусков</h3>
      <table className="products">
        <thead>
          <tr>
            <th>Тип</th>
            <th>Статус</th>
            <th>Старт</th>
            <th>Финиш</th>
            <th className="num">Элементов</th>
            <th>Ошибка</th>
          </tr>
        </thead>
        <tbody>
          {runs.data?.items.length === 0 && (
            <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 24 }}>
              Запусков ещё не было.
            </td></tr>
          )}
          {runs.data?.items.map((r) => (
            <tr key={r.id}>
              <td>{r.kind}</td>
              <td>{r.status}</td>
              <td className="muted">{new Date(r.startedAt).toLocaleString('ru-RU')}</td>
              <td className="muted">{r.finishedAt ? new Date(r.finishedAt).toLocaleString('ru-RU') : '—'}</td>
              <td className="num">{r.itemsProcessed}</td>
              <td className="muted" style={{ color: r.errorText ? 'var(--red)' : undefined }}>{r.errorText ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function RrpBlock() {
  const sync = useMutation({ mutationFn: api.rrpSync });
  const preview = useMutation({ mutationFn: api.rrpPreview });
  const enforce = useMutation({ mutationFn: () => api.rrpEnforce(false) });
  const [previewData, setPreviewData] = useState<RrpPreview | null>(null);

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>РРЦ-защита (рекомендованная розничная цена из 1С)</h3>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Если фактическая цена клиента на Ozon (с учётом Ozon-карты, акций) опустилась ниже РРЦ — поднимаем нашу цену пропорционально. Автоматически каждые 30 минут. Cooldown 60 мин на товар.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="secondary" onClick={() => sync.mutate()} loading={sync.isPending}>
          📥 Синк РРЦ из 1С
        </Button>
        <Button variant="secondary" onClick={() => preview.mutate(undefined, { onSuccess: (r) => setPreviewData(r) })} loading={preview.isPending}>
          👁 Превью защиты
        </Button>
        <Button variant="primary" onClick={() => {
          if (confirm('Запустить РРЦ-защиту? Будут обновлены цены товаров с нарушением.')) enforce.mutate();
        }} loading={enforce.isPending}>
          🛡 Запустить защиту сейчас
        </Button>
        {sync.data && <span style={{ color: 'var(--green)', fontSize: 13 }}>✓ Синк в очереди ({sync.data.jobId})</span>}
        {enforce.data && <span style={{ color: 'var(--green)', fontSize: 13 }}>✓ Защита в очереди ({enforce.data.jobId})</span>}
        {(sync.error || enforce.error || preview.error) && <span style={{ color: 'var(--red)', fontSize: 13 }}>Ошибка: {String(((sync.error || enforce.error || preview.error) as Error).message)}</span>}
      </div>

      {previewData && (
        <div style={{ marginTop: 16, padding: 12, background: '#fff7ed', borderRadius: 6, border: '1px solid #fed7aa' }}>
          <strong>Результат превью:</strong>
          <div style={{ marginTop: 8, fontSize: 13 }}>
            Проверено: {previewData.productsChecked} · Будут подняты: <strong>{previewData.productsAdjusted}</strong> · Пропущено: {previewData.productsSkipped}
          </div>
          {previewData.decisions.filter((d) => d.newPrice).length > 0 && (
            <table className="products" style={{ marginTop: 8 }}>
              <thead>
                <tr><th>Артикул</th><th className="num">Сейчас</th><th className="num">Эффект.</th><th className="num">РРЦ</th><th className="num">Новая</th><th>Причина</th></tr>
              </thead>
              <tbody>
                {previewData.decisions.filter((d) => d.newPrice).slice(0, 30).map((d) => (
                  <tr key={d.productId}>
                    <td>{d.offerId}</td>
                    <td className="num">{d.ourPrice}</td>
                    <td className="num" style={{ color: 'var(--red)' }}>{d.effective}</td>
                    <td className="num">{d.rrp}</td>
                    <td className="num" style={{ color: 'var(--green)', fontWeight: 600 }}>{d.newPrice}</td>
                    <td className="muted" style={{ fontSize: 12 }}>{d.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {previewData.decisions.filter((d) => d.newPrice).length > 30 && (
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              …показано первые 30 из {previewData.decisions.filter((d) => d.newPrice).length}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
